/* RichTextEditor.mui.tsx */
import { Stack, Typography } from '@mui/material'
import Placeholder from '@tiptap/extension-placeholder'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import Underline from '@tiptap/extension-underline'
import StarterKit from '@tiptap/starter-kit'
import {
  MenuButtonAddTable,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonUnderline,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  RichTextEditor,
  TableBubbleMenu,
  TableImproved,
  TableMenuControls
} from 'mui-tiptap'
import React, { useEffect } from 'react'

import { parseTextToHtmlParagraphs } from './richTextUtils'
import StyledRichTextEditor from './StyledRichTextEditor'

export type RichTextEditorMuiProps = {
  label?: string
  placeholder?: string
  disabled?: boolean
  content?: string
  onChange?: (html: string) => void
  error?: boolean
  helperText?: string
  value?: string
  onBlur?: () => void
  variant?: 'outlined' | 'standard'
}

const RichTextEditorMui: React.FC<RichTextEditorMuiProps> = ({
  label = 'Notiz',
  placeholder = 'Text hier eingeben …',
  disabled,
  value,
  onBlur,
  onChange,
  error = false,
  helperText
}) => {
  const editorRef = React.useRef<any>(null)

  useEffect(() => {
    const editor = editorRef.current?.editor
    if (!editor || editor.isDestroyed) {
      return
    }
    if (!editor.isFocused || !editor.isEditable) {
      // Use queueMicrotask per https://github.com/ueberdosis/tiptap/issues/3764#issuecomment-1546854730
      queueMicrotask(() => {
        const currentSelection = editor.state.selection
        editor
          .chain()
          .setContent(value || '')
          .setTextSelection(currentSelection)
          .run()
      })
    }
  }, [value, editorRef])
  return (
    <StyledRichTextEditor>
      <Stack spacing={1}>
        <Typography>{label}</Typography>
        <RichTextEditor
          content={parseTextToHtmlParagraphs(value)}
          onUpdate={({ editor }) => {
            const html = editor.getHTML()
            onChange?.(html)
          }}
          onBlur={onBlur}
          ref={editorRef}
          editable={!disabled}
          extensions={[
            StarterKit,
            Placeholder.configure({ placeholder }),
            Underline,
            TableImproved.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell
          ]}
          renderControls={() =>
            !disabled && (
              <MenuControlsContainer>
                <MenuSelectHeading />
                <MenuDivider />
                <MenuButtonBold />
                <MenuButtonItalic />
                <MenuButtonUnderline />
                <MenuDivider />
                <MenuButtonOrderedList />
                <MenuButtonBulletedList />
                <MenuDivider />
                <MenuButtonAddTable />
                <TableMenuControls />
              </MenuControlsContainer>
            )
          }
          RichTextFieldProps={{
            className: `rich-text-field ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`,
            variant: 'standard'
          }}
          className={`rich-text-editor ${error ? 'has-error' : ''} ${disabled ? 'disabled' : ''}`}
        >
          {() => !disabled && <TableBubbleMenu />}
        </RichTextEditor>
        {(error || helperText) && (
          <Typography
            variant="caption"
            color={error ? 'error' : 'textSecondary'}
            className="rich-text-helper-text"
          >
            {helperText}
          </Typography>
        )}
      </Stack>
    </StyledRichTextEditor>
  )
}

export default RichTextEditorMui
